import { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { apiVpnStar } from "./api/apiVpnStar";
import { DEFAULT_REGION, REGIONS } from "./config/config";
import { SiteDataProvider } from "./contexts/SiteDataContext";
import { cacheOperatorDefaultLocale } from "./i18n/detectLocale";
import { isAppLocale } from "./i18n/locales";
import MainPage from "./pages/MainPage/MainPage";
import ProtocolPage from "./pages/ProtocolPage/ProtocolPage";
import RatingPage from "./pages/RatingPage/RatingPage";
import type { Region, SiteData } from "./types";

function App() {
  const [siteData, setSiteData] = useState<SiteData>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const regionParam = urlParams.get("region");

    let initialRegion: Region | undefined = REGIONS.find(
      (region) => region.name === DEFAULT_REGION,
    );

    const savedRegion = localStorage.getItem("selectedRegion");
    if (savedRegion) {
      const parsedRegion = JSON.parse(savedRegion) as Region;
      const foundRegion = REGIONS.find((r) => r.code === parsedRegion.code);
      if (foundRegion) {
        initialRegion = foundRegion;
      }
    }

    if (regionParam) {
      const foundRegion = REGIONS.find((r) => r.code === regionParam);
      if (foundRegion) {
        initialRegion = foundRegion;
        localStorage.setItem("selectedRegion", JSON.stringify(foundRegion));
      }
    }

    return {
      region: initialRegion,
    };
  });

  useEffect(() => {
    /* istanbul ignore if */
    if (!siteData.region) return;
    localStorage.setItem("selectedRegion", JSON.stringify(siteData.region));
  }, [siteData.region]);

  useEffect(() => {
    Promise.all([apiVpnStar.getSiteConfig(), apiVpnStar.getRatingTypes()])
      .then(([siteConfig, ratingList]) => {
        if (isAppLocale(siteConfig.default_locale)) {
          cacheOperatorDefaultLocale(siteConfig.default_locale);
        }
        setSiteData((prevSiteData) => ({
          ...prevSiteData,
          ...siteConfig.site_texts,
          siteConfig,
          ratingList,
        }));
      })
      .catch((error) => {
        console.info("Error fetching site config or rating types:", error);
      });
  }, []);

  return (
    <SiteDataProvider siteData={siteData} setSiteData={setSiteData}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/rating" element={<RatingPage />} />
          <Route path="/protocol" element={<ProtocolPage />} />
        </Routes>
        <span className="pointer-events-none fixed bottom-1 right-2 z-40 select-none text-[10px] font-medium leading-none text-white/40">
          1.0.2
        </span>
      </Router>
    </SiteDataProvider>
  );
}

export default App;
